// Per-sport bootstrap seed template used by the API when an organization is
// created and by the create wizard's "Your divisions" step. Centralizes the
// default contest names (under a tournament) and the default division names,
// SEPARATELY for a league and for a tournament, so adding a new sport is a
// single map entry — same pattern as `sportCategory.ts` and `sportLabels.ts`.
//
// Names come from market research (owner 2026-10-22, ADR 0067): the most
// common division naming convention per sport, leagues and tournaments
// looked at separately. The earlier "UPPER / LOWER / OPEN DIVISION" set was
// invented and matched nothing organizers actually run.

export interface BootstrapDivision {
  name: string;
  // Higher renders first (describeCompetition / the wizard sort priority desc).
  priority: number;
}

export interface BootstrapStructure {
  // Contest (league) names seeded under a tournament; empty for a league.
  contests: string[];
  // Divisions seeded under the league, or under EACH contest of a tournament.
  divisions: BootstrapDivision[];
}

export interface BootstrapTemplate {
  league: BootstrapStructure;
  tournament: BootstrapStructure;
}

export type BootstrapContestType = 'tournament' | 'season';

/** Listed order = display order (first = highest priority). */
const divs = (names: string[]): BootstrapDivision[] =>
  names.map((name, i) => ({ name, priority: names.length - i }));

// Adult rec basketball leagues run skill letters (A = former varsity/college,
// B = competitive, C = rec); youth basketball tournaments split boys/girls by
// grade pairs.
const DEFAULT_TEMPLATE: BootstrapTemplate = {
  league: { contests: [], divisions: divs(['A Division', 'B Division', 'C Division']) },
  tournament: {
    contests: ['BOYS', 'GIRLS'],
    divisions: divs(['3rd-4th Grade', '5th-6th Grade', '7th-8th Grade']),
  },
};

const OVERRIDES: Record<string, BootstrapTemplate> = {
  basketball: DEFAULT_TEMPLATE,
  // Skill bands in 0.5 steps (self-rated / DUPR); tournaments run the three
  // USA Pickleball doubles events with the same bands under each.
  pickleball: {
    league: { contests: [], divisions: divs(['3.0', '3.5', '4.0', '4.5+']) },
    tournament: {
      contests: ['MENS DOUBLES', 'WOMENS DOUBLES', 'MIXED DOUBLES'],
      divisions: divs(['3.0', '3.5', '4.0', '4.5+']),
    },
  },
  // Rec leagues: A / BB / B / C; tournaments: the USAV ladder below Open.
  volleyball: {
    league: { contests: [], divisions: divs(['A Division', 'BB Division', 'B Division', 'C Division']) },
    tournament: { contests: ['MENS', 'WOMENS', 'COED'], divisions: divs(['AA', 'A', 'BB', 'B']) },
  },
  // Youth club flavor (owner 2026-08-31, confirmed by the research): age
  // groups, boys/girls at tournaments.
  soccer: {
    league: { contests: [], divisions: divs(['U10', 'U12', 'U14']) },
    tournament: { contests: ['BOYS', 'GIRLS'], divisions: divs(['U10', 'U12', 'U14']) },
  },
  // Adult slow pitch: leagues split Men's / Coed / Women's; USSSA / USA
  // Softball tournaments class teams C / D / E under Men's and Coed.
  softball: {
    league: { contests: [], divisions: divs(["Men's", 'Coed', "Women's"]) },
    tournament: { contests: ['MENS', 'COED'], divisions: divs(['C Division', 'D Division', 'E Division']) },
  },
  // Youth: Little League's ladder for a league; USSSA / travel tournaments
  // run age groups with AA / AAA / Major classes under each.
  baseball: {
    league: { contests: [], divisions: divs(['Tee Ball', 'Coach Pitch', 'Minors', 'Majors']) },
    tournament: { contests: ['10U', '12U', '14U'], divisions: divs(['AA', 'AAA', 'Major']) },
  },
  // Handicap flights for a league; flighted amateur events.
  golf: {
    league: { contests: [], divisions: divs(['A Flight', 'B Flight', 'C Flight']) },
    tournament: {
      contests: ['MENS', 'WOMENS', 'SENIORS'],
      divisions: divs(['Championship Flight', 'First Flight', 'Second Flight']),
    },
  },
};

export function getBootstrapTemplate(sport: string | null | undefined): BootstrapTemplate {
  const key = (sport ?? '').toLowerCase();
  return OVERRIDES[key] ?? DEFAULT_TEMPLATE;
}

/** The structure to seed for one competition kind (fresh arrays). */
export function getBootstrapStructure(sport: string | null | undefined, contestType: BootstrapContestType | 'league' | string): BootstrapStructure {
  const tpl = getBootstrapTemplate(sport);
  const s = contestType === 'tournament' ? tpl.tournament : tpl.league;
  return { contests: [...s.contests], divisions: s.divisions.map((d) => ({ ...d })) };
}

// Example teams seeded into ONE bootstrap division (owner 2026-09-15) so a
// brand-new org can use the scheduler (/admin/scheduler → Create Game /
// Auto-Populate) immediately — scheduling is impossible with zero teams.
// Names are REALISTIC rec-league names (owner 2026-09-16: "Team A/B/C/D
// looks terrible") for team sports; generic PERSON names for individual
// sports (pickleball is mostly doubles → "First Last / First Last" pairs,
// matching how auto-generated doubles teams display; golf/tennis get
// single names). Always 4, every format.
const EXAMPLE_DOUBLES_PAIRS = [
  'John Smith / Joe Jones',
  'Mike Johnson / Chris Lee',
  'Sam Davis / Alex Brown',
  'Pat Taylor / Jamie Wilson',
];

const EXAMPLE_SINGLES = ['John Smith', 'Joe Jones', 'Mike Johnson', 'Chris Lee'];

const EXAMPLE_TEAMS = ['Falcons', 'Wolves', 'Titans', 'Comets'];

export function getExampleTeamNames(sport: string | null | undefined): string[] {
  const key = (sport ?? '').toLowerCase();
  if (key === 'pickleball') return [...EXAMPLE_DOUBLES_PAIRS];
  if (key === 'golf' || key === 'tennis') return [...EXAMPLE_SINGLES];
  return [...EXAMPLE_TEAMS];
}
